/**
 * Ficha de cliente.
 *
 * Reúne lo que hasta ahora había que ir a buscar a cuatro pantallas: sus
 * centros, sus contratos, sus facturas y —lo que de verdad importa en este
 * sector— **si alguno de sus centros está incumpliendo**.
 *
 * Esa última pregunta es la que convierte la ficha en una herramienta
 * comercial: un cliente con tres centros vencidos y sin contrato no es un
 * dato administrativo, es una conversación pendiente.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSession } from '../../session/session-context.js';
import { ApiError } from '../../session/api-error.js';
import { DatumGroup, Datum, DetailShell, type Tab } from './detail-shell.js';
import { MiniList } from './mini-list.js';
import { AttachmentsPanel, type AttachmentItem } from '../../forms/attachments-panel.js';
import { money } from '../../table/format.js';

interface CustomerDetail {
  id: string;
  code: string;
  /** El alta vino del terminal y el código todavía es de andar por casa. */
  code_provisional: boolean;
  legal_name: string;
  trade_name: string | null;
  tax_id: string | null;
  customer_type: string;
  status: string;
  email: string | null;
  phone: string | null;
  payment_terms: string | null;
  notes: string | null;
  site_count: string;
  // Comunidad de propietarios
  property_manager_id: string | null;
  property_manager_name: string | null;
  president_name: string | null;
  president_phone: string | null;
  president_email: string | null;
  maintenance_contact_name: string | null;
  maintenance_contact_phone: string | null;
}

/**
 * Filas de las listas embebidas.
 *
 * Se declaran aunque sean parciales: con `any`, un nombre de campo mal
 * escrito se pintaría como vacío y nadie lo notaría hasta que alguien
 * preguntara por qué esa columna siempre sale en blanco.
 */
interface SiteRow {
  id: string;
  code: string;
  name: string;
  main_use: string;
  equipment_count: string;
  overdue_count: string;
  inspection_required: boolean | null;
  inspection_interval_years: number | null;
}

interface ContractRow {
  id: string;
  code: string;
  name: string;
  status: string;
  end_date: string | null;
  in_notice_window: boolean;
}

interface InvoiceRow {
  id: string;
  number: string;
  issue_date: string;
  status: string;
  due_date: string | null;
  days_overdue: number | null;
  total: string;
  currency: string;
}

const TYPE: Record<string, string> = {
  COMPANY: 'Empresa',
  INDIVIDUAL: 'Particular',
  PUBLIC_ADMIN: 'Administración pública',
};

const STATUS: Record<string, { label: string; tone: string; icon: string }> = {
  ACTIVE: { label: 'Activo', tone: 'ok', icon: '✓' },
  PROSPECT: { label: 'Potencial', tone: 'info', icon: '◦' },
  INACTIVE: { label: 'Inactivo', tone: 'off', icon: '•' },
  BLOCKED: { label: 'Bloqueado', tone: 'bad', icon: '⚠' },
};

export function CustomerDetailScreen(): React.ReactNode {
  const { api } = useSession();
  const { id } = useParams<{ id: string }>();

  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const [archivos, setArchivos] = useState<AttachmentItem[]>([]);

  const recargarArchivos = useCallback(async () => {
    if (id === undefined) return;
    try {
      const datos = await api.get<{ items: AttachmentItem[] }>(`/attachments/CUSTOMER/${id}`);
      setArchivos(datos.items);
    } catch {
      // Que falten los papeles no debe tumbar la ficha entera del cliente.
      setArchivos([]);
    }
  }, [api, id]);

  useEffect(() => {
    void recargarArchivos();
  }, [recargarArchivos]);

  const countSetter = useCallback(
    (key: string) => (total: number | null) => setCounts((c) => ({ ...c, [key]: total })),
    [],
  );

  useEffect(() => {
    let cancelled = false;

    api
      .get<CustomerDetail>(`/customers/${id}`)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(caught instanceof ApiError ? caught.message : 'No se ha podido cargar el cliente.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, id]);

  const status = STATUS[detail?.status ?? ''] ?? STATUS['ACTIVE']!;

  // Un alta firmada en la tablet llega con un código provisional: el técnico no
  // puede saber cuál toca sin conexión. Aquí se le da el que sigue a la serie,
  // que es lo que el cliente verá en su copia y en las facturas.
  const [asignando, setAsignando] = useState(false);
  const [avisoCodigo, setAvisoCodigo] = useState<string | null>(null);

  const asignarCodigo = useCallback(() => {
    setAsignando(true);
    setAvisoCodigo(null);
    api
      .post<{ code: string }>(`/customers/${id}/asignar-codigo`, {})
      .then((res) => {
        setDetail((d) => (d ? { ...d, code: res.code, code_provisional: false } : d));
        setAvisoCodigo(`Código asignado: ${res.code}`);
      })
      .catch((caught: unknown) => {
        setAvisoCodigo(
          caught instanceof ApiError ? caught.message : 'No se ha podido asignar el código.',
        );
      })
      .finally(() => setAsignando(false));
  }, [api, id]);

  const tabs: Tab[] = [
    {
      id: 'resumen',
      label: 'Resumen',
      render: () => (
        <DatumGroup title="Datos">
          <Datum
            label="Código"
            help={
              detail?.code_provisional
                ? 'Provisional: lo puso el terminal al firmar el contrato. Asígnale el definitivo antes de enviarle la copia al cliente.'
                : undefined
            }
          >
            {detail?.code}
            {detail?.code_provisional && <span className="pill pill--warn">Provisional</span>}
          </Datum>
          <Datum label="Razón social">{detail?.legal_name}</Datum>
          <Datum label="Nombre comercial">{detail?.trade_name}</Datum>
          <Datum
            label="NIF"
            help={detail?.tax_id ? undefined : 'Sin NIF no se le puede emitir una factura.'}
          >
            {detail?.tax_id}
          </Datum>
          <Datum label="Tipo">{TYPE[detail?.customer_type ?? ''] ?? detail?.customer_type}</Datum>
          <Datum label="Correo">{detail?.email}</Datum>
          <Datum label="Teléfono">{detail?.phone}</Datum>
          <Datum
            label="Condiciones de pago"
            help="La Ley 3/2004 fija 30 días por defecto y 60 como máximo pactable."
          >
            {detail?.payment_terms}
          </Datum>
          <Datum label="Notas">{detail?.notes}</Datum>
        </DatumGroup>
      ),
    },
    // Sólo tiene sentido en una comunidad: en una empresa serían huecos vacíos.
    ...(detail?.customer_type === 'COMMUNITY'
      ? [
          {
            id: 'comunidad',
            label: 'Comunidad',
            render: () => (
              <>
                <DatumGroup title="Administración">
                  <Datum
                    label="Administrador de fincas"
                    help={
                      detail?.property_manager_name
                        ? undefined
                        : 'Sin administrador no se puede agrupar esta comunidad con las demás de su gestoría.'
                    }
                  >
                    {detail?.property_manager_name}
                  </Datum>
                </DatumGroup>
                <DatumGroup title="Presidente">
                  <Datum label="Nombre">{detail?.president_name}</Datum>
                  <Datum label="Teléfono">{detail?.president_phone}</Datum>
                  <Datum label="Correo">{detail?.president_email}</Datum>
                </DatumGroup>
                <DatumGroup title="Mantenimiento">
                  <Datum
                    label="Contacto"
                    help="Quien abre la puerta el día de la revisión."
                  >
                    {detail?.maintenance_contact_name}
                  </Datum>
                  <Datum label="Teléfono">{detail?.maintenance_contact_phone}</Datum>
                </DatumGroup>
              </>
            ),
          } satisfies Tab,
        ]
      : []),
    {
      id: 'archivos',
      label: 'Archivos',
      render: () => (
        <AttachmentsPanel
          title="Documentos del cliente"
          note="Riesgos laborales firmados y entregados, y cualquier papel que haya que poder enseñar. En una comunidad, la PRL es lo que se pide primero."
          entityType="CUSTOMER"
          entityId={detail?.id ?? null}
          kind="PRL"
          items={archivos}
          onChanged={() => void recargarArchivos()}
        />
      ),
    },
    {
      id: 'centros',
      label: 'Centros',
      count: counts['centros'] ?? null,
      render: () => (
        <MiniList<SiteRow>
          path="/sites"
          params={{ customerId: id ?? '' }}
          seeAll={`/centros?q=${encodeURIComponent(detail?.legal_name ?? '')}`}
          emptyMessage="Este cliente no tiene centros dados de alta."
          onCount={countSetter('centros')}
          columns={[
            { label: 'Código', render: (r) => <Link to={`/centros/${r.id}`}>{r.code}</Link> },
            { label: 'Centro', render: (r) => r.name },
            { label: 'Uso', render: (r) => r.main_use },
            { label: 'Equipos', numeric: true, render: (r) => r.equipment_count },
            {
              label: 'Vencidos',
              numeric: true,
              render: (r) =>
                Number(r.overdue_count) > 0 ? (
                  <span className="due due--late">⚠ {r.overdue_count}</span>
                ) : (
                  <span className="due--none">0</span>
                ),
            },
            {
              label: 'Inspección periódica',
              render: (r) =>
                r.inspection_required === null ? (
                  <span className="pill pill--warn">? Sin determinar</span>
                ) : r.inspection_required ? (
                  <span className="pill pill--info">Cada {r.inspection_interval_years ?? '—'} años</span>
                ) : (
                  <span className="pill pill--off">No sujeto</span>
                ),
            },
          ]}
        />
      ),
    },
    {
      id: 'contratos',
      label: 'Contratos',
      count: counts['contratos'] ?? null,
      render: () => (
        <MiniList<ContractRow>
          path="/contracts"
          params={{ customerId: id ?? '' }}
          seeAll="/contratos?v=all"
          emptyMessage="Este cliente no tiene contratos. Si tiene equipos con obligaciones, es una oportunidad."
          onCount={countSetter('contratos')}
          columns={[
            // Enlazado, como los centros: desde aquí se llega a la ficha del
            // contrato, que es donde están la firma y la copia para el cliente.
            { label: 'Código', render: (r) => <Link to={`/contratos/${r.id}`}>{r.code}</Link> },
            { label: 'Contrato', render: (r) => <Link to={`/contratos/${r.id}`}>{r.name}</Link> },
            { label: 'Estado', render: (r) => r.status },
            {
              label: 'Vencimiento',
              render: (r) =>
                r.end_date === null ? (
                  <span className="due--none">Indefinido</span>
                ) : r.in_notice_window && r.status === 'ACTIVE' ? (
                  <span className="due due--soon">◷ Preaviso · {r.end_date}</span>
                ) : (
                  r.end_date
                ),
            },
          ]}
        />
      ),
    },
    {
      id: 'facturas',
      label: 'Facturas',
      count: counts['facturas'] ?? null,
      render: () => (
        <MiniList<InvoiceRow>
          path="/invoices"
          params={{ customerId: id ?? '' }}
          seeAll="/facturas"
          emptyMessage="Este cliente no tiene facturas."
          onCount={countSetter('facturas')}
          columns={[
            { label: 'Nº', render: (r) => <Link to={`/facturas/${r.id}`}>{r.number}</Link> },
            { label: 'Emitida', render: (r) => r.issue_date },
            { label: 'Estado', render: (r) => r.status },
            {
              label: 'Vencimiento',
              render: (r) =>
                r.status === 'EMITIDA' && (r.days_overdue ?? 0) > 0 ? (
                  <span className="due due--late">⚠ {r.days_overdue} d · {r.due_date}</span>
                ) : (
                  (r.due_date ?? '—')
                ),
            },
            { label: 'Total', numeric: true, render: (r) => money(r.total, r.currency) },
          ]}
        />
      ),
    },
  ];

  return (
    <DetailShell
      title={detail?.legal_name ?? 'Cliente'}
      subtitle={detail ? `${detail.code}${detail.tax_id ? ` · ${detail.tax_id}` : ''}` : undefined}
      badges={
        detail && <span className={`pill pill--${status.tone}`}>{`${status.icon} ${status.label}`}</span>
      }
      actions={
        <>
          <Link className="button button--quiet button--small" to={`/clientes/${id}/editar`}>
            Editar
          </Link>
          <Link className="button button--quiet button--small" to="/centros/nuevo">
            Nuevo centro
          </Link>
          {detail?.code_provisional && (
            <button
              type="button"
              className="button button--small"
              onClick={asignarCodigo}
              disabled={asignando}
            >
              {asignando ? 'Asignando…' : 'Asignar código definitivo'}
            </button>
          )}
          {avisoCodigo && <span className="hint">{avisoCodigo}</span>}
        </>
      }
      tabs={tabs}
      loading={loading}
      error={error}
    />
  );
}
